import { queriesAccounts, queriesPayStatements, queriesWorkLogs } from "@query";
import {
  WSAlert,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayStatementStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { WSQueries } from "../../../../query/WSQuery";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { useModalResourceDrillDownHistory } from "../../../../shared/components/ResourceHistory/ModalResourceDrillDownHistory";
import { routeHelpersWorkLogPayerDetails } from "../../../WorkLogs/paths";
import { PaystubDeductionsCard } from "../../components/PaystubDeductionsCard";
import { PaystubDetailsCard } from "../../components/PaystubDetailsCard";
import { PaystubIncomeCard } from "../../components/PaystubIncomeCard";
import { PaystubWorkLogsCard } from "../../components/PaystubWorkLogsCard";
import { useMutationDownloadPaystubPdf } from "../../hooks/useMutationDownloadPaystubPdf";
import { useModalReviewExcludedWorkLogs } from "../../modals/ModalReviewExcludedWorkLogs";
import {
  routeHelperPayrollRunsDashboard,
  routeHelperPayrollRunsDetails,
  routeHelperPayrollRunsPaystub
} from "../../paths";
import { selectorPaystubDetails } from "../../selectors/selectorPaystubDetails";
import { PayrollRunsYear } from "../../types";

export const RoutePayrollRunsPaystub: React.FC = () => {
  const goToDashboard = routeHelperPayrollRunsDashboard.useGoTo();
  const goToDetails = routeHelperPayrollRunsDetails.useGoTo();
  const goToWorkLogDetails = routeHelpersWorkLogPayerDetails.useGoTo();
  const [downloadPaystubPdf, downloadPaystubPdfMeta] =
    useMutationDownloadPaystubPdf();
  const modalReviewExcludedWorkLogs = useModalReviewExcludedWorkLogs();
  const modalResourceDrillDownHistory = useModalResourceDrillDownHistory();
  const {
    params: { year: yearParam, payrollRunId, paystubId }
  } = routeHelperPayrollRunsPaystub.useData();
  const year = yearParam as PayrollRunsYear;

  const queryPayStatement = queriesPayStatements.getPayStatement.useQuery({
    payStatementId: paystubId
  });
  const payPeriodId = queryPayStatement.data?.payPeriodId ?? undefined;

  // Secondary queries stay OUTSIDE `WSQueries` (as unconditional hooks) — each
  // is `enabled`-gated on the pay statement, so none becomes `isSuccess` on its
  // own and would otherwise keep `WSQueries` stuck on the loader. Their data is
  // consumed with graceful fallbacks in the render prop below.
  //
  // The work-logs API has no server-side pay-period filter, so we fetch the
  // (unscoped) list and filter client-side by payPeriodId to resolve this
  // statement's source work logs (and the submitted-but-unpaid ones).
  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQueryAll(
    {},
    { enabled: !!payPeriodId }
  );

  // The payee (name + address) and owning payer account ("Created by {company}")
  // are separate reads — the pay statement carries only their account ids.
  const payeeAccountId = queryPayStatement.data?.payeeAccountId ?? undefined;
  const payerAccountId = queryPayStatement.data?.payerAccountId ?? undefined;
  const queryPayeeAccount = queriesAccounts.getAccount.useQuery(
    { accountId: payeeAccountId ?? "" },
    { enabled: !!payeeAccountId }
  );
  const queryPayerAccount = queriesAccounts.getAccount.useQuery(
    { accountId: payerAccountId ?? "" },
    { enabled: !!payerAccountId }
  );

  return (
    <WSQueries queries={{ queryPayStatement }}>
      {({ queryPayStatementData }) => {
        const payPeriodWorkLogs = (queryWorkLogs.flatData ?? []).filter(
          workLog => workLog.payPeriodId === queryPayStatementData.payPeriodId
        );

        const paystub = selectorPaystubDetails({
          payStatement: queryPayStatementData,
          workLogs: payPeriodWorkLogs,
          payeeAccount: queryPayeeAccount.data,
          payerAccount: queryPayerAccount.data
        });

        // Before the run is processed the excluded work logs are still awaiting
        // approval (the pay may still change); once processed they simply
        // weren't paid.
        const isPendingApproval =
          queryPayStatementData.status === PayStatementStatus.DRAFT;

        const fileId = queryPayStatementData.fileId ?? undefined;
        const handleDownloadPdf = () =>
          fileId &&
          downloadPaystubPdf({
            fileId,
            filename: `paystub-${paystub.paystubNumber}`
          });
        const handleReviewExcludedWorkLogs = () =>
          modalReviewExcludedWorkLogs.open({
            workLogs: paystub.excludedWorkLogs,
            onWorkLogOpen: workLogId => goToWorkLogDetails({ workLogId })
          });

        return (
          <WSPage>
            <WSPageToolbar
              actions={[
                {
                  label: "Download PDF",
                  buttonKind: "Secondary",
                  disabled: !fileId,
                  loading: downloadPaystubPdfMeta.isLoading,
                  onClick: handleDownloadPdf
                },
                { separator: true },
                { label: "Download CSV", icon: "download" }
              ]}
              breadcrumbs={[
                {
                  label: "Payroll runs",
                  onClick: () => goToDashboard({ year })
                },
                {
                  label: "Payroll run",
                  onClick: () => goToDetails({ year, payrollRunId })
                },
                { label: `Paystub #${paystub.paystubNumber}` }
              ]}
              title={`Paystub #${paystub.paystubNumber}`}
              titleAfter={
                <WSText.ParagraphXs color="gray500">
                  Created by {paystub.createdBy}
                </WSText.ParagraphXs>
              }
            />

            {!!paystub.excludedWorkLogs.length &&
              (isPendingApproval ? (
                <WSAlert
                  action={{
                    text: "Review excluded work logs",
                    onClick: handleReviewExcludedWorkLogs
                  }}
                  icon="info-circle"
                  mt="L"
                  theme="info"
                  title="Work logs pending approval for this pay period"
                >
                  {`${paystub.excludedWorkLogs.length} work logs were submitted for this pay period but weren't approved yet. The total pay amount may change if these are approved for this pay period.`}
                </WSAlert>
              ) : (
                <WSAlert
                  action={{
                    text: "Review excluded work logs",
                    onClick: handleReviewExcludedWorkLogs
                  }}
                  icon="alert"
                  mt="L"
                  theme="warning"
                  title="Work logs submitted but not paid this period"
                >
                  {`${paystub.excludedWorkLogs.length} work logs were submitted for this pay period but weren't paid. Once a payroll run is finalized and paid, any work logs not included can only be paid through a separate off-cycle run.`}
                </WSAlert>
              ))}

            <WSGrid gutter="L" mt="XL">
              <WSGrid.Item span={{ s: "12", l: "8" }}>
                <WSList gap="XL">
                  <PaystubIncomeCard
                    earnings={paystub.earnings}
                    grossPayThisPeriod={paystub.grossPayThisPeriod}
                    grossPayYearToDate={paystub.grossPayYearToDate}
                  />
                  <PaystubDeductionsCard
                    deductionGroups={paystub.deductionGroups}
                    deductionTotalThisPeriod={paystub.deductionTotalThisPeriod}
                    deductionTotalYearToDate={paystub.deductionTotalYearToDate}
                    netPayThisPeriod={paystub.netPayThisPeriod}
                    netPayYearToDate={paystub.netPayYearToDate}
                    payoutMethodLabel={paystub.payoutMethodLabel}
                  />
                </WSList>
              </WSGrid.Item>

              <WSGrid.Item span={{ s: "12", l: "4" }}>
                <WSList gap="L">
                  <PaystubDetailsCard
                    employeeAddress={paystub.employeeAddress}
                    employeeName={paystub.employeeName}
                    paidDate={paystub.paidDate}
                    payPeriodRange={paystub.payPeriodRange}
                  />
                  <PaystubWorkLogsCard
                    workLogs={paystub.workLogs}
                    workLogsTotal={paystub.workLogsTotal}
                    onWorkLogOpen={workLogId =>
                      goToWorkLogDetails({ workLogId })
                    }
                  />
                  <ResourceHistory
                    resources={[
                      { resourceId: paystub.id, resourceType: "PayStatement" }
                    ]}
                    onViewAll={() =>
                      modalResourceDrillDownHistory.open({
                        resource: {
                          resourceId: paystub.id,
                          resourceType: "PayStatement"
                        },
                        title: "Paystub history"
                      })
                    }
                  />
                </WSList>
              </WSGrid.Item>
            </WSGrid>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
